/**
 * Pure pieces of the admin add-ons section on Store detail -> Billing (MOT-837):
 * what each billing mode says before an admin commits to it, and how a row reads.
 */
export type AddonBillingMode = 'billed' | 'free' | 'trial';

export interface AdminAddonRow {
  key: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: 'monthly' | 'yearly' | 'lifetime';
  includedInPlan: boolean;
  comingSoon: boolean;
  billingMode: AddonBillingMode | null;
  trialEndsOn: string | null;
  trialEndedOn: string | null;
  inactiveOnFreeTier: boolean;
  addedAt: string | null;
  addedBy: string | null;
  addBlockedReason: string | null;
  billedBlockedReason: string | null;
  trialBlockedReason: string | null;
  replacesBilled: Array<{ key: string; name: string }>;
}

export interface AdminAddonsResponse {
  enabled: boolean;
  tier?: string | null;
  planName?: string;
  hasStripeSubscription?: boolean;
  stripeTrialing?: boolean;
  addons?: AdminAddonRow[];
}

export const MODE_LABEL: Record<AddonBillingMode, string> = { billed: 'Billed', free: 'Free', trial: 'Trial' };

/** "$249/mo", in the stack's currency. A yearly or one-time add-on must never read as monthly. */
export function addonPriceLabel(row: Pick<AdminAddonRow, 'price' | 'currency' | 'interval'>): string {
  let amount: string;
  try {
    amount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: row.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(row.price);
  } catch {
    amount = `$${row.price}`;
  }
  if (row.interval === 'lifetime') return `${amount} once`;
  return `${amount}${row.interval === 'yearly' ? '/yr' : '/mo'}`;
}

/** A YYYY-MM-DD date as a reader sees it. Read as local noon, so a zone never moves it to the day before. */
export function addonDayLabel(day: string): string {
  const date = new Date(`${day}T12:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The header's coverage line. */
export function addonCoverageLine(rows: AdminAddonRow[]): string {
  const count = (mode: AddonBillingMode) => rows.filter((row) => row.billingMode === mode).length;
  const onStore = rows.filter((row) => row.billingMode !== null).length;
  if (onStore === 0) return `None of ${rows.length} on this store`;
  const parts = (['billed', 'free', 'trial'] as const)
    .filter((mode) => count(mode) > 0)
    .map((mode) => `${count(mode)} ${MODE_LABEL[mode].toLowerCase()}`);
  return `${onStore} of ${rows.length} on this store: ${parts.join(', ')}`;
}

/** Why a mode cannot be picked for this add-on right now, or null when it can. */
export function modeBlockedReason(row: AdminAddonRow, mode: AddonBillingMode): string | null {
  if (mode === 'billed') return row.billedBlockedReason;
  if (mode === 'trial') return row.trialBlockedReason;
  return null;
}

/** The mode a dialog opens on: Billed is the default, unless it cannot be used or is what the add-on already is. */
export function initialMode(row: AdminAddonRow): AddonBillingMode {
  const options = (['billed', 'free', 'trial'] as const).filter(
    (mode) => !modeBlockedReason(row, mode) && !(row.billingMode === mode && mode !== 'trial'),
  );
  return options[0] ?? 'free';
}

/** Modes a dialog offers: every one, less what this add-on already is (a Trial may be moved to a new date). */
export function modesOffered(row: AdminAddonRow): AddonBillingMode[] {
  return (['billed', 'free', 'trial'] as const).filter((mode) => row.billingMode !== mode || mode === 'trial');
}

/** What the admin is about to do, in words, including the charge. Shown in the dialog before it runs. */
export function addonChangeSummary(
  row: AdminAddonRow,
  mode: AddonBillingMode,
  options: { storeName: string; trialEndsOn?: string; stripeTrialing?: boolean },
): string[] {
  const lines: string[] = [];
  const price = addonPriceLabel(row);
  if (mode === 'billed') {
    lines.push(`This charges ${options.storeName} ${price} on its Stripe subscription. Stripe prorates the first invoice.`);
    if (row.interval === 'yearly') lines.push('This add-on bills once a year, not monthly.');
    if (options.stripeTrialing) lines.push('The Stripe subscription is in a trial, so the first charge comes when that trial ends.');
  } else if (mode === 'free') {
    lines.push('No charge. The add-on stays on until an admin removes it.');
  } else {
    const end = options.trialEndsOn ? addonDayLabel(options.trialEndsOn) : 'the end date';
    lines.push(`No charge through ${end}. After that the add-on ends and its features turn off. The store is not moved onto billing.`);
  }
  if (row.billingMode === 'billed' && mode !== 'billed') {
    lines.push(`The ${price} charge stops, and Stripe prorates it.`);
  }
  if (mode !== 'trial') {
    for (const replaced of row.replacesBilled) {
      lines.push(`This replaces ${replaced.name}, and its charge stops.`);
    }
  }
  return lines;
}

/** The submit button, which names the charge when there is one. */
export function addonSubmitLabel(row: AdminAddonRow, mode: AddonBillingMode): string {
  if (mode === 'billed') return `Charge ${addonPriceLabel(row)}`;
  if (row.billingMode === null) return mode === 'free' ? 'Add for free' : 'Start trial';
  return mode === 'free' ? 'Make free' : row.billingMode === 'trial' ? 'Update trial' : 'Move to trial';
}

/** The remove confirmation. */
export function addonRemoveMessage(row: AdminAddonRow, storeName: string): string {
  if (row.billingMode === 'billed') {
    return `${row.name} comes off ${storeName}, its features turn off, and the ${addonPriceLabel(row)} charge stops. Stripe prorates it.`;
  }
  if (row.billingMode === null) {
    return `This clears the ended trial of ${row.name} from ${storeName}. Nothing else changes.`;
  }
  return `${row.name} comes off ${storeName} and its features turn off. Nothing was being charged for it.`;
}
